/**
 * 系统消息 API 接口封装
 *
 * @description 封装所有系统消息相关的接口调用逻辑
 * - 采用混合架构：API层封装 + 内部调用长链接
 * - 保持与项目现有API模块一致的设计风格
 * - 利用WebSocket长链接实现实时通信
 *
 * @date 2025-09-16
 */
import { useBaseStore } from '@/stores'
import { ERROR_CODES, GLOBAL_URL } from '@/enums'
import type { SystemMessageResponse, MessageActionResponse, MessageActionRequest, SystemMessage, BatchOperationResult } from './types'

// 🔧 新增：引入模拟数据
import {
    getMockSystemMessages,
    getMockUnreadCount,
    getMockMessageById,
    getMockPopupMessages,
    mockMarkAsRead,
    mockBatchMarkAsRead,
    mockMarkAsPopped,
    mockAddNewMessage,
} from '@/mock/systemMessages'

/**
 * 测试场景类型
 */
export enum TestScenario {
    NORMAL = 'normal', // 正常数据
    EMPTY = 'empty', // 空数据
    NO_CHANGE = 'no_change', // 数据无变化
    HAS_NEW = 'has_new', // 有新数据
    STATUS_UPDATE = 'status_update', // 状态更新
}

/**
 * 开发环境配置
 * 🔧 TODO: 可以通过环境变量或配置文件控制
 */
const USE_MOCK_DATA = false // 开发阶段使用模拟数据
let CURRENT_SCENARIO: TestScenario = TestScenario.NORMAL // 当前测试场景

/**
 * 模拟网络请求延迟
 * @param min 最小延迟时间（毫秒），默认 500ms
 * @param max 最大延迟时间（毫秒），默认 1500ms
 * @returns Promise<void>
 */
const mockNetworkDelay = (min: number = 500, max: number = 1500): Promise<void> => {
    const delay = Math.floor(Math.random() * (max - min + 1)) + min
    console.log(`🌐 模拟网络延迟: ${delay}ms`)
    return new Promise((resolve) => setTimeout(resolve, delay))
}

/**
 * 场景控制方法
 */
export class ScenarioController {
    /**
     * 设置当前测试场景
     * @param scenario 场景类型
     */
    static setScenario(scenario: TestScenario): void {
        CURRENT_SCENARIO = scenario
        console.log(`🎬 切换测试场景: ${scenario}`)
    }

    /**
     * 获取当前测试场景
     */
    static getCurrentScenario(): TestScenario {
        return CURRENT_SCENARIO
    }

    /**
     * 根据场景获取对应的模拟数据
     * @param scenario 场景类型
     * @returns 模拟数据
     */
    static getScenarioData(scenario: TestScenario): SystemMessage[] {
        switch (scenario) {
            case TestScenario.EMPTY:
                console.log('📭 场景：空数据')
                return []

            case TestScenario.NO_CHANGE:
                console.log('🔄 场景：数据无变化（模拟正常请求）')
                // 📋 返回正常数据，模拟真实场景下的数据获取
                // 在真实项目中，即使数据没变化，API也会返回当前数据
                return getMockSystemMessages()

            case TestScenario.HAS_NEW:
                console.log('📥 场景：有新数据')
                // 添加一条新消息到原有数据前面
                mockAddNewMessage({ type: 'reward', isRead: false, needPopup: true })
                return getMockSystemMessages()

            case TestScenario.STATUS_UPDATE:
                console.log('✅ 场景：状态更新')
                const messages = getMockSystemMessages()
                // 模拟某些消息状态变化
                if (messages.length > 0) {
                    messages[0].isread = true // 第一条消息变为已读
                }
                if (messages.length > 1) {
                    messages[1].ispop = false // 第二条消息取消弹窗
                }
                return messages

            case TestScenario.NORMAL:
            default:
                console.log('📋 场景：正常数据')
                return getMockSystemMessages()
        }
    }
}

/**
 * 系统消息 API 类
 *
 * @description 使用静态方法封装所有系统消息相关的接口
 * 内部统一使用baseStore的长链接方法，保证实时性
 */
export class SystemMessageAPI {
    /**
     * 获取所有系统消息
     *
     * @description 调用 hybird.friends.systemmsg 接口获取系统消息列表
     * 🔧 支持模拟数据模式
     * @param loading 是否显示加载状态，默认true
     * @param tip 是否显示错误提示，默认true
     * @returns Promise<SystemMessageResponse> 系统消息响应数据
     */
    static async getAllSystemMessages(loading: boolean = true, tip: boolean = true): Promise<SystemMessageResponse> {
        try {
            // 🔧 开发阶段使用模拟数据
            if (USE_MOCK_DATA) {
                console.log('🎭 使用模拟数据 - 获取系统消息列表')
                await mockNetworkDelay(800, 2000) // 模拟较长的延迟，模拟获取列表的耗时

                // 🎬 根据当前场景返回对应的模拟数据
                const mockMessages = ScenarioController.getScenarioData(CURRENT_SCENARIO)

                return {
                    code: ERROR_CODES.OK,
                    channel: 'system',
                    sysmsgs: mockMessages,
                }
            }

            // 真实API调用
            const store = useBaseStore()
            const result: SystemMessageResponse = await store.request(GLOBAL_URL.GetAllSystemMsg, {}, { loading, tip })

            if (result.code === ERROR_CODES.OK) {
                return result
            } else {
                return {
                    code: -1,
                    channel: '',
                    sysmsgs: [],
                }
            }
        } catch (error) {
            // 返回空的响应格式，保证调用方可以正常处理
            return {
                code: -1,
                channel: '',
                sysmsgs: [],
            }
        }
    }

    /**
     * 标记消息为已读
     *
     * @description 调用 hybird.friends.systemmsgread 接口标记单条消息已读
     * 🔧 支持模拟数据模式
     * @param id 消息ID
     * @param loading 是否显示加载状态，默认false
     * @param tip 是否显示错误提示，默认false
     * @returns Promise<MessageActionResponse> 操作响应数据
     */
    static async markAsRead(id: number, loading: boolean = false, tip: boolean = false): Promise<MessageActionResponse> {
        try {
            // 🔧 开发阶段使用模拟数据
            if (USE_MOCK_DATA) {
                console.log('🎭 使用模拟数据 - 标记消息已读:', id)
                await mockNetworkDelay(200, 600) // 模拟快速操作的延迟
                const success = mockMarkAsRead(id)
                return {
                    code: success ? ERROR_CODES.OK : -1,
                    sysmsg: {} as SystemMessage, // 模拟数据不需要返回完整消息
                }
            }

            // 真实API调用
            const store = useBaseStore()
            const params: MessageActionRequest = { id }

            const result: MessageActionResponse = await store.request(GLOBAL_URL.SystemMsgRead, params, { loading, tip })

            if (result.code === ERROR_CODES.OK) {
                return result
            } else {
                return {
                    code: -1,
                    sysmsg: {} as SystemMessage,
                }
            }
        } catch (error) {
            return {
                code: -1,
                sysmsg: {} as SystemMessage,
            }
        }
    }

    /**
     * 标记消息为已弹框
     *
     * @description 调用 hybird.friends.systemmsgpop 接口标记消息已弹框
     * 🔧 支持模拟数据模式
     * @param id 消息ID
     * @param loading 是否显示加载状态，默认false
     * @param tip 是否显示错误提示，默认false
     * @returns Promise<MessageActionResponse> 操作响应数据
     */
    static async markAsPopped(id: number, loading: boolean = false, tip: boolean = false): Promise<MessageActionResponse> {
        try {
            // 🔧 开发阶段使用模拟数据
            if (USE_MOCK_DATA) {
                console.log('🎭 使用模拟数据 - 标记消息已弹框:', id)
                await mockNetworkDelay(200, 600) // 模拟快速操作的延迟
                const success = mockMarkAsPopped(id)
                return {
                    code: success ? ERROR_CODES.OK : -1,
                    sysmsg: {} as SystemMessage, // 模拟数据不需要返回完整消息
                }
            }

            // 真实API调用
            const store = useBaseStore()
            const params: MessageActionRequest = { id }

            const result: MessageActionResponse = await store.request(GLOBAL_URL.SystemMsgPop, params, { loading, tip })

            if (result.code === ERROR_CODES.OK) {
                return result
            } else {
                return {
                    code: -1,
                    sysmsg: {} as SystemMessage,
                }
            }
        } catch (error) {
            return {
                code: -1,
                sysmsg: {} as SystemMessage,
            }
        }
    }

    /**
     * 批量标记消息为已读
     *
     * @description 批量调用已读接口，提供详细的成功/失败统计
     * 🔧 支持模拟数据模式
     * @param ids 消息ID数组
     * @param concurrent 并发执行数量限制，默认5
     * @returns Promise<BatchOperationResult> 批量操作结果
     */
    static async batchMarkAsRead(ids: number[], concurrent: number = 5): Promise<BatchOperationResult> {
        if (ids.length === 0) {
            return { success: [], failed: [], errors: {} }
        }

        // 🔧 开发阶段使用模拟数据
        if (USE_MOCK_DATA) {
            console.log('🎭 使用模拟数据 - 批量标记消息已读:', ids)
            await mockNetworkDelay(500, 1200) // 模拟批量操作的延迟
            const successCount = mockBatchMarkAsRead(ids)
            const successIds = ids.slice(0, successCount)
            const failedIds = ids.slice(successCount)

            return {
                success: successIds,
                failed: failedIds,
                errors: failedIds.reduce((acc, id) => {
                    acc[id] = '模拟失败'
                    return acc
                }, {} as Record<number, string>),
            }
        }

        // 真实API调用
        const result: BatchOperationResult = {
            success: [],
            failed: [],
            errors: {},
        }

        // 分批处理，避免过多并发请求
        const chunks = []
        for (let i = 0; i < ids.length; i += concurrent) {
            chunks.push(ids.slice(i, i + concurrent))
        }

        for (const chunk of chunks) {
            const promises = chunk.map(async (id) => {
                const response = await this.markAsRead(id)
                if (response.code === ERROR_CODES.OK) {
                    result.success.push(id)
                } else {
                    result.failed.push(id)
                    result.errors![id] = `code:${response.code}`
                }
            })

            // 等待当前批次完成再处理下一批
            await Promise.all(promises)
        }

        return result
    }
}

// ==================== 🎭 开发测试便捷方法 ====================

/**
 * 开发测试工具集
 * 提供便捷的场景切换和测试方法
 */
export const DevTestUtils = {
    /**
     * 快速切换到空数据场景
     */
    switchToEmpty: () => ScenarioController.setScenario(TestScenario.EMPTY),

    /**
     * 快速切换到正常数据场景
     */
    switchToNormal: () => ScenarioController.setScenario(TestScenario.NORMAL),

    /**
     * 快速切换到无变化场景
     */
    switchToNoChange: () => ScenarioController.setScenario(TestScenario.NO_CHANGE),

    /**
     * 快速切换到有新数据场景
     */
    switchToHasNew: () => ScenarioController.setScenario(TestScenario.HAS_NEW),

    /**
     * 快速切换到状态更新场景
     */
    switchToStatusUpdate: () => ScenarioController.setScenario(TestScenario.STATUS_UPDATE),

    /**
     * 获取当前场景信息
     */
    getCurrentScenario: () => {
        const scenario = ScenarioController.getCurrentScenario()
        console.log(`🎬 当前场景: ${scenario}`)
        return scenario
    },

    /**
     * 重置消息计数器（用于测试）
     */
    resetMessageCounter: () => {
        // 重置模拟数据的消息计数器
        if (typeof window !== 'undefined' && (window as any).resetMessageCounter) {
            ;(window as any).resetMessageCounter()
        }
        console.log('🔄 消息计数器已重置')
    },

    /**
     * 模拟系统消息推送 - 奖励类型
     * @param delay 延迟时间（毫秒），默认 1000ms
     */
    pushRewardMessage: async (delay = 1000) => {
        if (typeof window !== 'undefined' && (window as any).mockPushSystemMessage) {
            console.log('🎁 模拟推送奖励消息...')
            return await (window as any).mockPushSystemMessage({ type: 'reward', delay })
        } else {
            console.error('❌ 推送功能未准备就绪，请确保在开发环境中运行')
        }
    },

    /**
     * 模拟系统消息推送 - 通知类型
     * @param delay 延迟时间（毫秒），默认 1000ms
     */
    pushNoticeMessage: async (delay = 1000) => {
        if (typeof window !== 'undefined' && (window as any).mockPushSystemMessage) {
            console.log('📢 模拟推送通知消息...')
            return await (window as any).mockPushSystemMessage({ type: 'notice', needPopup: false, delay })
        } else {
            console.error('❌ 推送功能未准备就绪，请确保在开发环境中运行')
        }
    },

    /**
     * 模拟系统消息推送 - 公告类型
     * @param delay 延迟时间（毫秒），默认 1000ms
     */
    pushAnnouncementMessage: async (delay = 1000) => {
        if (typeof window !== 'undefined' && (window as any).mockPushSystemMessage) {
            console.log('📣 模拟推送公告消息...')
            return await (window as any).mockPushSystemMessage({ type: 'announcement', delay })
        } else {
            console.error('❌ 推送功能未准备就绪，请确保在开发环境中运行')
        }
    },

    /**
     * 自定义模拟推送
     * @param options 推送配置
     */
    pushCustomMessage: async (options = {}) => {
        if (typeof window !== 'undefined' && (window as any).mockPushSystemMessage) {
            console.log('🔧 模拟自定义推送消息...', options)
            return await (window as any).mockPushSystemMessage(options)
        } else {
            console.error('❌ 推送功能未准备就绪，请确保在开发环境中运行')
        }
    },

    /**
     * 测试所有场景
     */
    testAllScenarios: async () => {
        const scenarios = Object.values(TestScenario)
        console.log('🧪 开始测试所有场景...')

        for (const scenario of scenarios) {
            console.log(`\n🎭 测试场景: ${scenario}`)
            ScenarioController.setScenario(scenario)
            try {
                const result = await SystemMessageAPI.getAllSystemMessages(false, false)
                console.log(`✅ 场景 ${scenario} 测试成功，返回 ${result.sysmsgs.length} 条消息`)
            } catch (error) {
                console.error(`❌ 场景 ${scenario} 测试失败:`, error)
            }
        }

        // 恢复到正常场景
        ScenarioController.setScenario(TestScenario.NORMAL)
        console.log('\n🔄 所有场景测试完成，已恢复到正常场景')
    },

    /**
     * 测试系统消息推送流程
     */
    testPushFlow: async () => {
        console.log('🚀 开始测试系统消息推送流程...')

        console.log('\n1️⃣ 测试奖励消息推送（有弹窗）')
        await DevTestUtils.pushRewardMessage(500)

        await new Promise((resolve) => setTimeout(resolve, 2000)) // 等待2秒

        console.log('\n2️⃣ 测试通知消息推送（无弹窗）')
        await DevTestUtils.pushNoticeMessage(500)

        await new Promise((resolve) => setTimeout(resolve, 2000)) // 等待2秒

        console.log('\n3️⃣ 测试公告消息推送（有弹窗）')
        await DevTestUtils.pushAnnouncementMessage(500)

        console.log('\n🎉 推送流程测试完成！请检查弹框显示和数据更新')
    },
}

/**
 * 导出默认实例（兼容性考虑）
 */
export default SystemMessageAPI

/**
 * 导出所有类型定义
 */
export * from './types'
